import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FileText, Calendar, Share2, Plus, Clock, Copy, Trash2, ExternalLink, Loader2, Mail, X, Send } from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { ReportBody } from "../reports/ReportBlocks";

const API = process.env.REACT_APP_BACKEND_URL;

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("narwhal_auth_token") || localStorage.getItem("token") || ""}`,
});

async function fetchReports(clientId) {
  const r = await axios.get(`${API}/api/clients/${clientId}/reports`, { headers: authHeader() });
  return r.data;
}
async function createReport(clientId, body) {
  const r = await axios.post(`${API}/api/clients/${clientId}/reports`, body, { headers: authHeader() });
  return r.data;
}
async function deleteReport(clientId, reportId) {
  await axios.delete(`${API}/api/clients/${clientId}/reports/${reportId}`, { headers: authHeader() });
}
async function fetchRecipients(clientId) {
  const r = await axios.get(`${API}/api/clients/${clientId}/reports-recipients`, { headers: authHeader() });
  return r.data || { recipients: [], default_note: "" };
}
async function saveRecipients(clientId, body) {
  const r = await axios.put(`${API}/api/clients/${clientId}/reports-recipients`, body, { headers: authHeader() });
  return r.data;
}
async function sendReportEmail(shareToken, body) {
  const r = await axios.post(`${API}/api/reports/${shareToken}/send`, body, { headers: authHeader() });
  return r.data;
}
async function previewReportEmail(shareToken, body) {
  const r = await axios.post(`${API}/api/reports/${shareToken}/preview-email`, body, { headers: authHeader() });
  return r.data;
}
async function fetchEmailTemplate() {
  const r = await axios.get(`${API}/api/admin/email-template`, { headers: authHeader() });
  return r.data;
}

const isoToday = () => new Date().toISOString().slice(0, 10);
const isoDaysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("es-AR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

export default function ReportsTab({ clientId, clientName }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState("weekly");
  const [openReport, setOpenReport] = useState(null);
  const [sendReport, setSendReport] = useState(null);
  const reports = useQuery({ queryKey: ["reports", clientId], queryFn: () => fetchReports(clientId), enabled: !!clientId });

  const gen = useMutation({
    mutationFn: () => {
      const days = kind === "weekly" ? 7 : 30;
      return createReport(clientId, { kind, period_start: isoDaysAgo(days), period_end: isoToday(), notes: "" });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["reports", clientId] }); toast.success("Reporte generado"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo generar"),
  });

  const del = useMutation({
    mutationFn: (id) => deleteReport(clientId, id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["reports", clientId] }); toast.success("Reporte eliminado"); },
  });

  const filtered = (reports.data || []).filter((r) => r.kind === kind);

  return (
    <div className="space-y-6" data-testid="reports-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-lg font-semibold text-slate-900">Reportes</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Cierres semanales y mensuales con snapshot congelado · Compartilos por link único.
          </p>
        </div>
        <Button
          data-testid="reports-generate-btn"
          onClick={() => gen.mutate()}
          disabled={gen.isPending}
          className="gap-2 bg-slate-900 hover:bg-slate-800 text-white"
        >
          {gen.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Generar reporte {kind === "weekly" ? "semanal" : "mensual"}
        </Button>
      </div>

      <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-1">
        {[
          { key: "weekly", label: "Semanales", icon: Calendar },
          { key: "monthly", label: "Mensuales", icon: FileText },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setKind(key)}
            data-testid={`reports-tab-${key}`}
            className={`inline-flex items-center gap-2 px-4 py-1.5 rounded text-sm font-medium transition-colors ${
              kind === key ? "bg-white shadow-sm text-slate-900" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
      </div>

      {reports.isLoading ? (
        <div className="py-16 text-center text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Cargando…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-md px-6 py-16 text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center mb-3">
            <Clock className="h-5 w-5 text-slate-400" />
          </div>
          <h3 className="font-display text-base font-semibold text-slate-900 mb-1">Sin reportes {kind === "weekly" ? "semanales" : "mensuales"} todavía</h3>
          <p className="text-sm text-slate-500 max-w-md mx-auto">Tocá &quot;Generar reporte&quot; para armar el primero con la data actual de {clientName || "este cliente"}.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((r) => (
            <ReportCard
              key={r.id}
              report={r}
              onOpen={() => setOpenReport(r)}
              onDelete={() => del.mutate(r.id)}
              onSend={() => setSendReport(r)}
            />
          ))}
        </div>
      )}

      {openReport && (
        <ReportDrawer
          report={openReport}
          onClose={() => setOpenReport(null)}
          onSend={() => { setSendReport(openReport); }}
        />
      )}
      {sendReport && (
        <SendEmailModal
          report={sendReport}
          clientId={clientId}
          clientName={clientName}
          onClose={() => setSendReport(null)}
        />
      )}
    </div>
  );
}

function ReportCard({ report, onOpen, onDelete, onSend }) {
  const shareUrl = `${window.location.origin}/r/${report.share_token}`;
  const copyLink = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(shareUrl);
    toast.success("Link copiado");
  };
  const lastSent = (report.email_log && report.email_log.length)
    ? report.email_log[report.email_log.length - 1]
    : null;
  return (
    <div
      onClick={onOpen}
      className="bg-white border border-slate-200 rounded-md p-4 flex items-center justify-between hover:border-slate-300 transition-colors cursor-pointer"
      data-testid={`report-card-${report.id}`}
    >
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-md bg-slate-50 border border-slate-100 flex items-center justify-center">
          {report.kind === "weekly" ? <Calendar className="h-4 w-4 text-slate-500" /> : <FileText className="h-4 w-4 text-slate-500" />}
        </div>
        <div>
          <div className="text-sm font-semibold text-slate-900">
            {report.kind === "weekly" ? "Semanal" : "Mensual"} · {fmtDate(report.period_start)} → {fmtDate(report.period_end)}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Generado {fmtDate(report.generated_at)} por {report.generated_by || "—"}
            {lastSent && (
              <span className="ml-2 text-emerald-600">· Último envío {fmtDate(lastSent.sent_at)} a {lastSent.to?.length || 0} destinatario{(lastSent.to?.length || 0) === 1 ? "" : "s"}</span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onSend}
          className="text-xs text-slate-900 hover:text-white hover:bg-slate-900 inline-flex items-center gap-1 px-2.5 py-1.5 rounded border border-slate-900 font-medium transition-colors"
          data-testid={`report-send-${report.id}`}
        >
          <Mail className="h-3.5 w-3.5" /> Enviar
        </button>
        <button onClick={copyLink} className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-50" data-testid={`report-copy-${report.id}`}>
          <Copy className="h-3.5 w-3.5" /> Copiar link
        </button>
        <a href={shareUrl} target="_blank" rel="noreferrer" className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-50">
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
        <button onClick={() => window.confirm("¿Eliminar?") && onDelete()} className="text-xs text-rose-600 hover:text-rose-700 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-rose-50">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function ReportDrawer({ report, onClose, onSend }) {
  const shareUrl = `${window.location.origin}/r/${report.share_token}`;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50" onClick={onClose}>
      <div className="absolute inset-y-0 right-0 w-full max-w-4xl bg-white shadow-xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-sm font-semibold text-slate-900">Reporte {report.kind === "weekly" ? "semanal" : "mensual"}</div>
            <div className="text-xs text-slate-500 mt-0.5">{fmtDate(report.period_start)} → {fmtDate(report.period_end)}</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onSend}
              className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-slate-900 text-white hover:bg-slate-800 font-medium"
              data-testid="report-drawer-send"
            >
              <Mail className="h-3.5 w-3.5" /> Enviar por email
            </button>
            <button onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Link copiado"); }} className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-slate-200 hover:bg-slate-50">
              <Share2 className="h-3.5 w-3.5" /> Compartir
            </button>
            <button onClick={onClose} className="text-slate-500 hover:text-slate-900 text-xl leading-none" data-testid="report-drawer-close">×</button>
          </div>
        </div>

        <div className="px-6 py-6 bg-slate-50">
          <ReportBody snapshot={report.snapshot} kind={report.kind} periodStart={report.period_start} periodEnd={report.period_end} />
          {report.notes && (
            <section className="bg-white rounded-md border border-slate-200 p-5 mt-4">
              <h3 className="font-display text-sm font-semibold text-slate-900 mb-3">Notas del account manager</h3>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{report.notes}</p>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Modal para enviar reporte por email ────────────────────────────────
function SendEmailModal({ report, clientId, clientName, onClose }) {
  const shareUrl = `${window.location.origin}/r/${report.share_token}`;
  const [emails, setEmails] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [note, setNote] = useState("");
  const [noteInitialized, setNoteInitialized] = useState(false);
  const [subject, setSubject] = useState(
    `Reporte ${report.kind === "weekly" ? "semanal" : "mensual"} ${clientName || ""} · ${report.period_start} → ${report.period_end}`.trim()
  );
  const [saveDefault, setSaveDefault] = useState(false);
  const qc = useQueryClient();

  const recips = useQuery({
    queryKey: ["report-recipients", clientId],
    queryFn: () => fetchRecipients(clientId),
    enabled: !!clientId,
  });
  const tpl = useQuery({
    queryKey: ["email-template"],
    queryFn: fetchEmailTemplate,
  });

  // Precarga defaults al abrir. Prioridad: default_note del cliente > template global > vacío.
  useEffect(() => {
    if (emails.length === 0 && recips.data?.recipients?.length) {
      setEmails(recips.data.recipients);
    }
    if (!noteInitialized) {
      const seed = recips.data?.default_note || tpl.data?.body || tpl.data?.default || "";
      if (seed) { setNote(seed); setNoteInitialized(true); }
    }
  }, [recips.data, tpl.data, emails.length, noteInitialized]);

  const preview = useQuery({
    queryKey: ["email-preview", report.share_token, note, subject],
    queryFn: () => previewReportEmail(report.share_token, { recipients: ["preview@example.com"], subject, note }),
    enabled: noteInitialized,
    staleTime: 0,
  });

  const isValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e || "");

  const addEmail = () => {
    const parts = emailInput.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean);
    const next = [...emails];
    for (const p of parts) {
      if (isValid(p) && !next.includes(p)) next.push(p);
    }
    setEmails(next);
    setEmailInput("");
  };
  const removeEmail = (e) => setEmails(emails.filter((x) => x !== e));

  const saveDefaults = useMutation({
    mutationFn: () => saveRecipients(clientId, { recipients: emails, default_note: note || "" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["report-recipients", clientId] }),
  });

  const send = useMutation({
    mutationFn: () => sendReportEmail(report.share_token, {
      recipients: emails,
      subject: subject || undefined,
      note: note || undefined,
    }),
    onSuccess: async () => {
      if (saveDefault) {
        try { await saveDefaults.mutateAsync(); } catch { /* silencio */ }
      }
      qc.invalidateQueries({ queryKey: ["reports", clientId] });
      toast.success(`Reporte enviado a ${emails.length} destinatario${emails.length === 1 ? "" : "s"}`);
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo enviar el reporte"),
  });

  const canSend = emails.length > 0 && !send.isPending;

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/60 flex items-center justify-center p-4" onClick={onClose} data-testid="send-report-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <Mail className="h-4 w-4 text-slate-600" /> Enviar reporte por email
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {clientName} · {fmtDate(report.period_start)} → {fmtDate(report.period_end)}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="send-modal-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-2 gap-0 overflow-hidden">
          {/* ── Columna izquierda: form ── */}
          <div className="px-6 py-5 space-y-5 overflow-y-auto border-r border-slate-100">
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Asunto</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:border-slate-400"
                data-testid="send-subject-input"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1.5 block">
                Destinatarios <span className="text-slate-400 font-normal">({emails.length})</span>
              </label>
              <div className="border border-slate-200 rounded p-2 flex flex-wrap gap-1.5 min-h-[42px]">
                {emails.map((e) => (
                  <span key={e} className="inline-flex items-center gap-1 bg-slate-100 text-slate-800 text-xs px-2 py-1 rounded" data-testid={`send-chip-${e}`}>
                    {e}
                    <button onClick={() => removeEmail(e)} className="text-slate-500 hover:text-slate-900">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === "," || e.key === " ") { e.preventDefault(); addEmail(); }
                    else if (e.key === "Backspace" && !emailInput && emails.length) { removeEmail(emails[emails.length - 1]); }
                  }}
                  onBlur={() => { if (emailInput) addEmail(); }}
                  placeholder={emails.length === 0 ? "email@dominio.com  (Enter para agregar)" : "Agregar otro…"}
                  className="flex-1 min-w-[180px] text-sm outline-none bg-transparent px-1"
                  data-testid="send-emails-input"
                />
              </div>
              {recips.data?.recipients?.length > 0 && (
                <div className="text-[11px] text-slate-500 mt-1">
                  Cargados desde el default del cliente. Marcá &quot;Guardar como default&quot; para persistir cambios.
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-slate-700">Cuerpo del mensaje</label>
                <button
                  type="button"
                  onClick={() => setNote(tpl.data?.default || "")}
                  className="text-[11px] text-slate-500 hover:text-slate-900 underline"
                  data-testid="send-reset-template-btn"
                >
                  Restablecer template
                </button>
              </div>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={9}
                placeholder={tpl.data?.default}
                className="w-full px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:border-slate-400 resize-none font-normal leading-relaxed"
                data-testid="send-note-input"
              />
              <div className="text-[11px] text-slate-500 mt-1">
                El link al reporte se agrega automáticamente como botón debajo. Doble Enter = párrafo.
              </div>
            </div>

            <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={saveDefault}
                onChange={(e) => setSaveDefault(e.target.checked)}
                className="rounded border-slate-300 mt-0.5"
                data-testid="send-save-default"
              />
              <span>Guardar estos destinatarios y este cuerpo como <b>default</b> para futuros reportes de {clientName}</span>
            </label>
          </div>

          {/* ── Columna derecha: preview real ── */}
          <div className="bg-slate-50 flex flex-col overflow-hidden">
            <div className="px-4 py-2 bg-slate-100 border-b border-slate-200 text-[11px] font-semibold text-slate-600 uppercase tracking-wide flex items-center justify-between">
              <span>Preview del email</span>
              {preview.isFetching && <span className="text-slate-400 normal-case font-normal">Actualizando…</span>}
            </div>
            <div className="px-4 py-2 text-xs text-slate-600 border-b border-slate-200 bg-white space-y-0.5">
              <div><span className="text-slate-400">De:</span> Narwhal Hub · Reportes</div>
              <div><span className="text-slate-400">Para:</span> {emails.length ? emails.join(", ") : <span className="text-slate-300 italic">agregá destinatarios</span>}</div>
              <div><span className="text-slate-400">Asunto:</span> <span className="text-slate-900">{subject}</span></div>
            </div>
            <div className="flex-1 min-h-[420px] overflow-y-auto bg-slate-100 p-2">
              {preview.data?.html ? (
                <iframe
                  srcDoc={preview.data.html}
                  className="w-full h-full min-h-[400px] bg-white border-0 rounded"
                  title="Email preview"
                  sandbox=""
                  data-testid="send-email-preview"
                />
              ) : (
                <div className="text-center text-xs text-slate-400 py-10">Cargando preview…</div>
              )}
            </div>
            <div className="px-4 py-2 border-t border-slate-200 text-[11px] text-slate-500 truncate bg-white">
              CTA lleva a: <a href={shareUrl} target="_blank" rel="noreferrer" className="text-slate-700 underline break-all">{shareUrl}</a>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50">
          <div className="text-[11px] text-slate-500">
            Envío desde Gmail SMTP · Reply-to = tu email de sesión
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="text-sm px-4 py-2 rounded border border-slate-200 hover:bg-white" data-testid="send-cancel-btn">Cancelar</button>
            <Button
              onClick={() => send.mutate()}
              disabled={!canSend}
              className="gap-2 bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-50"
              data-testid="send-confirm-btn"
            >
              {send.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Enviar reporte
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
